import { useState, useEffect, forwardRef } from 'react';
import noop from 'utils/noop';
import { AnimatedRefreshIcon } from 'ui/icons/aurora/animated/AnimatedRefreshIcon';
import { Button, ButtonProps } from '../Button';
import { BUTTON_VARIANTS, BUTTON_SIZES } from '../constants';
import { IconButton } from '../IconButton';
import { ContentWrapper } from './RefreshButton.styles';

interface RefreshButtonProps extends ButtonProps {
  /** Callback when the button is clicked */
  onClick?: (e: any) => void;
  /** The size of the icon */
  iconSize?: number;
  /** Any additional props to pass to the icon */
  iconProps?: any;
  /**
   * Is a refresh in progress?
   * This prop allows the start/stop of the refresh animation to be set
   * externally, allowing for a continued animation if desired
   */
  isRefreshing?: boolean;
  /** Is the button disabled? */
  isDisabled?: ButtonProps['isDisabled'];
}

export const RefreshButton = forwardRef(function RefreshButton(
  {
    onClick = noop,
    iconSize = 16,
    iconProps = null,
    isRefreshing,
    isDisabled = false,
    children,
    ...props
  }: RefreshButtonProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const [refreshIsRotating, setRefreshIsRotating] = useState(false);

  /**
   * Determines whether refresh is controlled by a prop, instead of
   * internal state
   */
  const refreshIsControlled = typeof isRefreshing === 'boolean';

  useEffect(() => {
    if (refreshIsControlled) {
      setRefreshIsRotating(isRefreshing);
    }
  }, [isRefreshing]);

  const ButtonComponent = children ? Button : IconButton;

  return (
    <ButtonComponent
      variant={BUTTON_VARIANTS.GHOST}
      size={BUTTON_SIZES.SMALL}
      onClick={(e) => {
        if (isDisabled) return;
        if (!refreshIsControlled) setRefreshIsRotating(true);

        onClick(e);
      }}
      isDisabled={isDisabled}
      ref={ref}
      {...props}
    >
      <AnimatedRefreshIcon
        $isAnimating={refreshIsRotating}
        $isAnimationInfinite={refreshIsControlled}
        onAnimationEnd={() => !refreshIsControlled && setRefreshIsRotating(false)}
        width={iconSize}
        height={iconSize}
        {...iconProps}
      />
      {children && <ContentWrapper>{children}</ContentWrapper>}
    </ButtonComponent>
  );
});
